import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Clock, LogOut, RefreshCw } from 'lucide-react';

/**
 * SessionTimeoutWarning - modal shown shortly before the user's session
 * is about to expire. Counts down `minutesRemaining` and offers
 * "Stay logged in" (which calls onStayLoggedIn) or "Log out".
 *
 * Stateless beyond the local countdown - open/close is controlled by the parent.
 */
export default function SessionTimeoutWarning({
  isOpen = false,
  minutesRemaining = 2,
  onStayLoggedIn,
  onLogOut,
}) {
  const [secondsLeft, setSecondsLeft] = useState(minutesRemaining * 60);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSecondsLeft(minutesRemaining * 60);
  }, [isOpen, minutesRemaining]);

  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          if (typeof onLogOut === 'function') {
            try { onLogOut(); } catch (_) { /* ignore */ }
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isOpen, onLogOut]);

  if (!isOpen) return null;

  const mm = Math.floor(secondsLeft / 60);
  const ss = secondsLeft % 60;
  const display = `${mm}:${ss.toString().padStart(2, '0')}`;

  const handleStay = async () => {
    if (typeof onStayLoggedIn !== 'function') return;
    setWorking(true);
    try {
      await onStayLoggedIn();
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
      data-testid="session-timeout-warning-modal"
    >
      <div className="w-full max-w-sm bg-[var(--safekept-card)] rounded-2xl p-6 border border-[var(--safekept-border)] shadow-2xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <h2
            id="session-timeout-title"
            className="text-lg font-bold text-[var(--safekept-text)]"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Still there?
          </h2>
        </div>

        <p className="text-sm text-[var(--safekept-text-secondary)] mb-4">
          Your session will expire automatically in{' '}
          <span
            className="font-mono font-semibold text-[var(--safekept-text)]"
            data-testid="session-timeout-countdown"
          >
            {display}
          </span>
          . Choose <strong>Stay logged in</strong> to keep working, or sign out now.
        </p>

        <div className="flex flex-col gap-2">
          <Button
            onClick={handleStay}
            disabled={working}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl min-h-[44px]"
            data-testid="session-timeout-stay-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${working ? 'animate-spin' : ''}`} />
            Stay logged in
          </Button>
          <Button
            variant="outline"
            onClick={onLogOut}
            className="w-full rounded-xl min-h-[44px]"
            data-testid="session-timeout-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </Button>
        </div>
      </div>
    </div>
  );
}
